'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslations } from 'next-intl';

import styles from './V2AnnouncementStrip.module.css';

const C_V2_VIDEO_SOURCES = [
  {
    src: 'https://cdn.trendex.one/videos/TrenDex.One_Live%20Predictions-V2.mp4',
    type: 'video/mp4'
  },
  {
    src: 'https://cdn.trendex.one/videos/TrenDex.One_Live%20Predictions-V2.mov',
    type: 'video/quicktime'
  }
];
const C_V2_VIDEO_POSTER_URL = '/assets/images/videoPosters/tdx-v2-thumb.png';
const C_PARTICLE_COUNT = 14;
const C_CHART_STEP = 4;
const C_FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])';

function PlayIcon() {
  return (
    <svg
      width="24"
      height="26"
      viewBox="0 0 24 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M22.5 11.27c1.33.77 1.33 2.69 0 3.46L3.75 25.56C2.42 26.33.75 25.37.75 23.83V2.17C.75.63 2.42-.33 3.75.44L22.5 11.27z"
        fill="#fff"
      />
    </svg>
  );
}

export function V2AnnouncementStrip() {
  const t = useTranslations('home.v2Strip');
  const tAccessibility = useTranslations('accessibility');
  const stripRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lightboxRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const getLightboxFocusableElements = useCallback(() => {
    if (!lightboxRef.current) return [];

    return Array.from(
      lightboxRef.current.querySelectorAll<HTMLElement>(C_FOCUSABLE_SELECTOR)
    ).filter(element => element.getClientRects().length > 0);
  }, []);

  useEffect(() => {
    const strip = stripRef.current;
    const bg = bgRef.current;

    if (!strip || !bg) return;

    const handlePointerMove = (event: PointerEvent) => {
      const rect = strip.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;

      bg.style.transform = `translate(${x * -26}px,${y * -18}px)`;
    };

    const handlePointerLeave = () => {
      bg.style.transform = 'translate(0,0)';
    };

    const particles = Array.from({ length: C_PARTICLE_COUNT }, () => {
      const particle = document.createElement('span');
      particle.className = styles.particle;
      particle.style.left = `${4 + Math.random() * 92}%`;
      particle.style.bottom = '0';
      particle.style.animationDuration = `${9 + Math.random() * 10}s`;
      particle.style.animationDelay = `${-Math.random() * 14}s`;
      bg.appendChild(particle);
      return particle;
    });

    strip.addEventListener('pointermove', handlePointerMove);
    strip.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      strip.removeEventListener('pointermove', handlePointerMove);
      strip.removeEventListener('pointerleave', handlePointerLeave);
      particles.forEach(particle => particle.remove());
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const strip = stripRef.current;
    const context = canvas?.getContext('2d');

    if (!canvas || !strip || !context) return;

    const size = () => {
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
    };

    size();
    window.addEventListener('resize', size);

    const points: number[] = [];
    let time = 0;
    let drift = 0;
    let last: number | null = (canvas.height || 585) * 0.97;
    const phaseOne = Math.random() * 9;
    const phaseTwo = Math.random() * 9;
    let animationFrame = 0;

    const tick = (height: number) => {
      time += 1;
      drift += (Math.random() - 0.47) * 1.6;
      drift = Math.max(Math.min(drift, height * 0.11), -height * 0.06);

      const wave =
        Math.sin(time * 0.016 + phaseOne) * height * 0.09 +
        Math.sin(time * 0.006 + phaseTwo) * height * 0.11 +
        Math.sin(time * 0.031 + phaseOne + phaseTwo) * height * 0.035;
      const target = height * 0.66 - drift - wave + (Math.random() - 0.5) * 1.2;

      last = last === null ? target : last * 0.9 + target * 0.1;
      points.push(last);
    };

    for (let i = 0; i < 6; i += 1) {
      tick(canvas.height || 585);
    }

    const step = () => {
      const width = canvas.width;
      const height = canvas.height;

      tick(height);

      const span = Math.floor((width * 0.72) / C_CHART_STEP);
      if (points.length > span) points.shift();

      context.clearRect(0, 0, width, height);

      const startX = width * 0.14;

      context.beginPath();
      for (let i = 0; i < points.length; i += 1) {
        const x = startX + i * C_CHART_STEP;
        if (i) {
          context.lineTo(x, points[i]);
        } else {
          context.moveTo(x, points[i]);
        }
      }

      const lastX = startX + (points.length - 1) * C_CHART_STEP;
      const lastY = points[points.length - 1];

      context.lineTo(lastX, height);
      context.lineTo(startX, height);
      context.closePath();

      const gradient = context.createLinearGradient(
        0,
        height * 0.35,
        0,
        height
      );
      gradient.addColorStop(0, 'rgba(54,211,153,.14)');
      gradient.addColorStop(1, 'rgba(54,211,153,0)');
      context.fillStyle = gradient;
      context.fill();

      context.beginPath();
      for (let i = 0; i < points.length; i += 1) {
        const x = startX + i * C_CHART_STEP;
        if (i) {
          context.lineTo(x, points[i]);
        } else {
          context.moveTo(x, points[i]);
        }
      }
      context.strokeStyle = 'rgba(54,211,153,.85)';
      context.lineWidth = 2.5;
      context.lineJoin = 'round';
      context.shadowColor = 'rgba(54,211,153,.8)';
      context.shadowBlur = 10;
      context.stroke();
      context.shadowBlur = 0;

      const pulse = (time % 90) / 90;
      context.beginPath();
      context.arc(lastX, lastY, 4.5, 0, 7);
      context.fillStyle = '#7df0c4';
      context.fill();

      context.beginPath();
      context.arc(lastX, lastY, 4.5 + pulse * 16, 0, 7);
      context.strokeStyle = `rgba(54,211,153,${0.5 * (1 - pulse)})`;
      context.lineWidth = 1.5;
      context.stroke();

      animationFrame = requestAnimationFrame(step);
    };

    animationFrame = requestAnimationFrame(step);

    return () => {
      window.removeEventListener('resize', size);
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusFallback = () => {
      closeButtonRef.current?.focus();
    };

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const lightbox = lightboxRef.current;
      const focusableElements = getLightboxFocusableElements();

      if (!lightbox || !focusableElements.length) {
        event.preventDefault();
        focusFallback();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;
      const hasFocusInside =
        activeElement instanceof Node && lightbox.contains(activeElement);

      if (!hasFocusInside) {
        event.preventDefault();
        (event.shiftKey ? lastElement : firstElement).focus();
        return;
      }

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      const lightbox = lightboxRef.current;
      const target = event.target;

      if (lightbox && target instanceof Node && !lightbox.contains(target)) {
        event.stopPropagation();
        focusFallback();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.body.style.overflow = previousOverflow;
    };
  }, [getLightboxFocusableElements, isOpen]);

  return (
    <>
      <section className={styles.v2strip} id="v2strip" ref={stripRef}>
        <div className={styles.bgwrap} id="bgwrap" ref={bgRef}>
          <div className={`${styles.blob} ${styles.b1}`} />
          <div className={`${styles.blob} ${styles.b2}`} />
          <div className={`${styles.blob} ${styles.b3}`} />
          <div className={`${styles.blob} ${styles.b4}`} />
          <div className={styles.grid} />
          <canvas className={styles.chartbg} id="chartbg" ref={canvasRef} />
        </div>

        <div className={styles.inner}>
          <div className={styles.copy}>
            <span className={styles.kick}>
              <span className={styles.dot} />
              {t('eyebrow')}
            </span>

            <h2 className={styles.title}>
              {t('titlePrefix')} <span className={styles.v2}>v2</span>{' '}
              {t('titleSuffix')}
            </h2>

            <p className={styles.desc}>{t('description')}</p>

            <div className={styles.meanwhile}>{t('meanwhile')}</div>

            <a
              className={styles.paBtn}
              href="https://www.predictarena.io"
              target="_blank"
              rel="noopener"
            >
              <span className={styles.ball}>&#9917;</span>
              <span className={styles.paText}>
                <span className={styles.paLineOne}>
                  {t('predictArenaLineOne')}
                </span>{' '}
                <span className={styles.paLineTwo}>
                  {t('predictArenaLineTwo')}
                </span>
              </span>
            </a>
          </div>

          <DialogPrimitive.Root open={isOpen} onOpenChange={setIsOpen} modal>
            <div className={styles.vid}>
              <DialogPrimitive.Trigger asChild>
                <button
                  className={styles.vframe}
                  title={t('videoTitle')}
                  type="button"
                >
                  <div className={styles.sheen} />
                  <div className={styles.play}>
                    <PlayIcon />
                  </div>
                </button>
              </DialogPrimitive.Trigger>

              <div className={styles.vcap}>
                <span>
                  <b>{t('videoName')}</b> &middot; {t('videoSubtitle')}
                </span>
                <span>{t('videoDuration')}</span>
              </div>
            </div>

            <DialogPrimitive.Portal>
              <DialogPrimitive.Overlay className={styles.lbOverlay} />
              <DialogPrimitive.Content
                ref={lightboxRef}
                className={styles.lbContent}
                onOpenAutoFocus={event => {
                  event.preventDefault();
                  closeButtonRef.current?.focus();
                }}
              >
                <DialogPrimitive.Title className="sr-only">
                  {t('videoDialogLabel')}
                </DialogPrimitive.Title>

                <div className={styles.lbBox}>
                  {isOpen && (
                    <video
                      controls
                      autoPlay
                      playsInline
                      poster={C_V2_VIDEO_POSTER_URL}
                      tabIndex={0}
                    >
                      {C_V2_VIDEO_SOURCES.map(source => (
                        <source
                          key={source.src}
                          src={source.src}
                          type={source.type}
                        />
                      ))}
                    </video>
                  )}
                </div>

                <DialogPrimitive.Close asChild>
                  <button
                    ref={closeButtonRef}
                    className={styles.lbClose}
                    type="button"
                    aria-label={tAccessibility('close')}
                  >
                    &times;
                  </button>
                </DialogPrimitive.Close>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          </DialogPrimitive.Root>
        </div>
      </section>
    </>
  );
}
